import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Trash2, Upload, Plus, X, ExternalLink, Image as ImageIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const AUDIENCES = [
  { id: "couples", label: "Couples" },
  { id: "tv", label: "TV" },
  { id: "single_guys", label: "Single Guys" },
  { id: "single_ladies", label: "Single Ladies" },
];

const MEMBERSHIP_TYPES = [
  { id: "single_guy", label: "Single Man" },
  { id: "single_lady", label: "Single Lady" },
  { id: "couple", label: "Couple" },
  { id: "tv", label: "TV" },
];

const RECURRENCE_OPTIONS = [
  { id: "none", label: "One-off (no recurrence)" },
  { id: "every_week", label: "Every week (same weekday)" },
  { id: "monthly_nth", label: "The Nth [weekday] of each month" },
];

const ON_SALE_OPTIONS = [
  { id: "always", label: "Always on sale" },
  { id: "7_days", label: "7 days before event" },
  { id: "14_days", label: "14 days before event" },
  { id: "21_days", label: "21 days before event" },
  { id: "specific_date", label: "Specific date" },
];

const NTH_OPTIONS = [
  { v: 1, label: "1st" },
  { v: 2, label: "2nd" },
  { v: 3, label: "3rd" },
  { v: 4, label: "4th" },
  { v: 5, label: "5th (if the month has one)" },
];

const inputCls =
  "bg-black border-white/15 rounded-none h-11 text-[#F5F5F0] placeholder:text-[#A3A3A3]/60 focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0 w-full px-3";

export default function PortalEventEdit() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: "",
    first_date: "",
    time: "22:00",
    recurrence: "none",
    recurrence_nth: 2,
    theme: "",
    on_sale: "always",
    on_sale_date: "",
    audiences: ["couples"],
    pricing: { couples: 0 },
    discount_codes: [],
    photo_data_uri: null,
    slug: null,
    capacity: "",
    tickets_sold: 0,
    visibility_mode: "count",
    visibility_count: 4,
    visibility_until: "",
    occurrence_overrides: [],
    require_membership: false,
    membership_pricing: {},
  });
  const [origSlug, setOrigSlug] = useState(null);
  const [occurrences, setOccurrences] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const meRes = await portalMe();
      setMe(meRes);
      if (!isNew) {
        const { data } = await portalApi.get(`/portal/events/${id}`);
        setForm({
          name: data.name || "",
          first_date: data.first_date || "",
          time: data.time || "22:00",
          recurrence: data.recurrence || "none",
          recurrence_nth: data.recurrence_nth || 2,
          theme: data.theme || "",
          on_sale: data.on_sale || "always",
          on_sale_date: data.on_sale_date || "",
          audiences: data.audiences || [],
          pricing: data.pricing || {},
          discount_codes: data.discount_codes || [],
          photo_data_uri: data.photo_data_uri || null,
          slug: data.slug || null,
          capacity: data.capacity ?? "",
          tickets_sold: data.tickets_sold ?? 0,
          visibility_mode: data.visibility_mode || "count",
          visibility_count: data.visibility_count ?? 4,
          visibility_until: data.visibility_until || "",
          occurrence_overrides: data.occurrence_overrides || [],
          require_membership: !!data.require_membership,
          membership_pricing: data.membership_pricing || {},
        });
        setOrigSlug(data.slug || null);
      }
    } catch {
      toast.error("Failed to load event.");
      nav("/portal", { replace: true });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
     
  }, [id]);

  // Live client-side occurrence generation — updates instantly as the user edits visibility fields.
  const computedOccurrences = useMemo(() => {
    if (form.recurrence === "none" || !form.first_date) return [];
    const todayIso = new Date().toISOString().slice(0, 10);
    const firstDt = new Date(form.first_date + "T00:00:00");
    if (isNaN(firstDt.getTime())) return [];
    const dates = [];

    if (form.recurrence === "every_week") {
      let d = new Date(firstDt);
      const cap = 500;
      for (let i = 0; i < cap; i += 1) {
        const iso = d.toISOString().slice(0, 10);
        dates.push(iso);
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7);
        if (form.visibility_mode === "until_date" && form.visibility_until && iso > form.visibility_until) break;
        if (form.visibility_mode === "count") {
          const future = dates.filter((x) => x >= todayIso).length;
          if (future >= Number(form.visibility_count || 4)) break;
        }
      }
    } else if (form.recurrence === "monthly_nth") {
      const n = Math.max(1, Math.min(5, Number(form.recurrence_nth || 1)));
      const weekday = firstDt.getDay(); // JS Sun=0..Sat=6
      let y = firstDt.getFullYear();
      let m = firstDt.getMonth();
      const capMonths = 36;
      for (let i = 0; i < capMonths; i += 1) {
        // First day of month
        const first = new Date(y, m, 1);
        const firstDow = first.getDay();
        const delta = (weekday - firstDow + 7) % 7;
        const day = 1 + delta + (n - 1) * 7;
        const candidate = new Date(y, m, day);
        // Ensure it's still in same month
        if (candidate.getMonth() === m) {
          const iso = candidate.toISOString().slice(0, 10);
          dates.push(iso);
        }
        m += 1;
        if (m > 11) {
          m = 0;
          y += 1;
        }
        if (
          form.visibility_mode === "until_date" &&
          form.visibility_until &&
          dates.length > 0 &&
          dates[dates.length - 1] > form.visibility_until
        )
          break;
      }
    }

    let future = dates.filter((d) => d >= todayIso);
    if (form.visibility_mode === "count") {
      future = future.slice(0, Math.max(1, Math.min(52, Number(form.visibility_count || 4))));
    } else if (form.visibility_mode === "until_date" && form.visibility_until) {
      future = future.filter((d) => d <= form.visibility_until);
    }
    return future.map((d) => {
      const override = (form.occurrence_overrides || []).find((o) => o.date === d);
      return { date: d, theme: override?.theme ?? form.theme ?? null };
    });
  }, [
    form.recurrence,
    form.recurrence_nth,
    form.first_date,
    form.visibility_mode,
    form.visibility_count,
    form.visibility_until,
    form.theme,
    form.occurrence_overrides,
  ]);

  // Keep the `occurrences` state in sync with the live computed list so the rest of the form uses one source.
  useEffect(() => {
    setOccurrences(computedOccurrences);
  }, [computedOccurrences]);

  const toggleAudience = (aid) => {
    setForm((f) => {
      const has = f.audiences.includes(aid);
      const audiences = has ? f.audiences.filter((a) => a !== aid) : [...f.audiences, aid];
      const pricing = { ...f.pricing };
      if (!has && pricing[aid] === undefined) pricing[aid] = 0;
      if (has) delete pricing[aid];
      return { ...f, audiences, pricing };
    });
  };

  const setPrice = (aid, val) => {
    const n = val === "" ? "" : Number(val);
    setForm((f) => ({ ...f, pricing: { ...f.pricing, [aid]: isNaN(n) ? "" : n } }));
  };

  const addDiscount = () => {
    setForm((f) => ({ ...f, discount_codes: [...f.discount_codes, { code: "", saving: 0 }] }));
  };
  const updateDiscount = (i, patch) => {
    setForm((f) => ({
      ...f,
      discount_codes: f.discount_codes.map((d, idx) => (idx === i ? { ...d, ...patch } : d)),
    }));
  };
  const removeDiscount = (i) => {
    setForm((f) => ({ ...f, discount_codes: f.discount_codes.filter((_, idx) => idx !== i) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name || !form.first_date || !form.time) {
      toast.error("Name, date and time are required.");
      return;
    }
    if (form.audiences.length === 0) {
      toast.error("Select at least one audience type.");
      return;
    }
    if (form.recurrence === "monthly_nth" && !form.recurrence_nth) {
      toast.error("Choose which occurrence of the weekday (e.g. 2nd).");
      return;
    }
    if (form.on_sale === "specific_date" && !form.on_sale_date) {
      toast.error("Enter the on-sale date.");
      return;
    }
    // Clean pricing: keep only entries for selected audiences
    const cleanPricing = {};
    form.audiences.forEach((a) => {
      cleanPricing[a] = Number(form.pricing[a] || 0);
    });
    // Clean discount codes
    const cleanDiscounts = form.discount_codes
      .filter((d) => d.code && String(d.code).trim() !== "")
      .map((d) => ({ code: String(d.code).trim(), saving: Number(d.saving) || 0 }));

    const payload = {
      name: form.name.trim(),
      first_date: form.first_date,
      time: form.time,
      recurrence: form.recurrence,
      recurrence_nth: form.recurrence === "monthly_nth" ? form.recurrence_nth : null,
      theme: form.theme.trim() || null,
      on_sale: form.on_sale,
      on_sale_date: form.on_sale === "specific_date" ? form.on_sale_date : null,
      audiences: form.audiences,
      pricing: cleanPricing,
      discount_codes: cleanDiscounts,
      capacity: form.capacity === "" ? null : Number(form.capacity),
      tickets_sold: Number(form.tickets_sold) || 0,
      visibility_mode: form.recurrence === "none" ? "count" : form.visibility_mode,
      visibility_count:
        form.visibility_mode === "count" ? Math.max(1, Math.min(52, Number(form.visibility_count) || 4)) : 4,
      visibility_until:
        form.visibility_mode === "until_date" && form.visibility_until ? form.visibility_until : null,
      occurrence_overrides: (form.occurrence_overrides || [])
        .filter((o) => o.date && ((o.theme || "").trim() || o.photo_data_uri))
        .map((o) => ({
          date: o.date,
          theme: (o.theme || "").trim() || null,
          photo_data_uri: o.photo_data_uri || null,
        })),
      require_membership: !!form.require_membership,
      membership_pricing: form.require_membership
        ? Object.fromEntries(
            MEMBERSHIP_TYPES.map((m) => [m.id, Number(form.membership_pricing?.[m.id] || 0)])
          )
        : {},
    };

    setSaving(true);
    try {
      let eventId = id;
      if (isNew) {
        const { data } = await portalApi.post("/portal/events", payload);
        eventId = data.id;
        toast.success("Event created.");
      } else {
        await portalApi.patch(`/portal/events/${id}`, payload);
        toast.success("Event saved.");
      }
      if (isNew) nav(`/portal/events/${eventId}`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try {
      await portalApi.delete(`/portal/events/${id}`);
      toast.success("Deleted.");
      nav("/portal");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const inputRef = useRef(null);
  const uploadPhoto = async (file) => {
    if (!file || uploading) return;
    if (isNew) {
      toast.error("Save the event first, then you can upload a photo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await portalApi.post(`/portal/events/${id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_data_uri: data.photo_data_uri }));
      toast.success("Photo uploaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed.";
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <PortalShell me={me}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell me={me} testId="portal-event-edit-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/portal"
            data-testid="event-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          {!isNew && (
            <div className="flex items-center gap-3">
              {me?.venue?.slug && origSlug && (
                <a
                  href={`/v/${me.venue.slug}/${origSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="event-view-public"
                  className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
                >
                  <ExternalLink size={12} /> View public page
                </a>
              )}
              <button
                type="button"
                onClick={deleteEvent}
                data-testid="event-delete"
                className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          )}
        </div>

        <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
          {isNew ? "New event" : "Edit event"}
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-10">
          {isNew ? "Create an event" : form.name}
        </h1>

        <form onSubmit={submit} className="space-y-8" data-testid="event-form" noValidate>
          {/* 1. Name */}
          <Section title="Details">
            <Field label="Event name *">
              <Input
                data-testid="event-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Saturday Social"
                className={inputCls}
                required
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 2. Date */}
              <Field label="First date *">
                <Input
                  type="date"
                  data-testid="event-date"
                  value={form.first_date}
                  onChange={(e) => setForm({ ...form, first_date: e.target.value })}
                  className={inputCls}
                  required
                />
              </Field>
              {/* 4. Time */}
              <Field label="Time *">
                <Input
                  type="time"
                  data-testid="event-time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className={inputCls}
                  required
                />
              </Field>
            </div>

            {/* 3. Recurrence */}
            <Field label="Recurrence">
              <select
                data-testid="event-recurrence"
                value={form.recurrence}
                onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                className={inputCls}
              >
                {RECURRENCE_OPTIONS.map((r) => (
                  <option key={r.id} value={r.id} className="bg-black">{r.label}</option>
                ))}
              </select>
            </Field>

            {form.recurrence === "monthly_nth" && (
              <Field label="Which occurrence of the weekday? *">
                <select
                  data-testid="event-recurrence-nth"
                  value={form.recurrence_nth}
                  onChange={(e) => setForm({ ...form, recurrence_nth: Number(e.target.value) })}
                  className={inputCls}
                >
                  {NTH_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v} className="bg-black">{o.label}</option>
                  ))}
                </select>
              </Field>
            )}

            {/* 5. Theme */}
            <Field label="Theme (optional — e.g. Masquerade, Neon Night)">
              <Input
                data-testid="event-theme"
                value={form.theme}
                onChange={(e) => setForm({ ...form, theme: e.target.value })}
                placeholder=""
                className={inputCls}
              />
            </Field>
          </Section>

          {/* 5b. Visibility + per-occurrence themes (recurring only) */}
          {form.recurrence !== "none" && (
            <Section title="Visibility">
              <Field label="Show recurring occurrences on your page as…">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="visibility-mode-count"
                    onClick={() => setForm({ ...form, visibility_mode: "count" })}
                    className={`h-11 border text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${
                      form.visibility_mode === "count"
                        ? "bg-[#D9A6F5] text-black border-[#D9A6F5]"
                        : "bg-black border-white/15 text-[#A3A3A3] hover:text-[#F5F5F0]"
                    }`}
                  >
                    Next N events
                  </button>
                  <button
                    type="button"
                    data-testid="visibility-mode-until"
                    onClick={() => setForm({ ...form, visibility_mode: "until_date" })}
                    className={`h-11 border text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${
                      form.visibility_mode === "until_date"
                        ? "bg-[#D9A6F5] text-black border-[#D9A6F5]"
                        : "bg-black border-white/15 text-[#A3A3A3] hover:text-[#F5F5F0]"
                    }`}
                  >
                    Until a specific date
                  </button>
                </div>
              </Field>

              {form.visibility_mode === "count" && (
                <Field label="How many upcoming occurrences to show">
                  <Input
                    type="number"
                    min={1}
                    max={52}
                    value={form.visibility_count}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        visibility_count: Math.max(1, Math.min(52, parseInt(e.target.value || "4", 10) || 4)),
                      })
                    }
                    data-testid="visibility-count"
                    className={inputCls}
                  />
                </Field>
              )}
              {form.visibility_mode === "until_date" && (
                <Field label="Show occurrences up to (inclusive)">
                  <Input
                    type="date"
                    value={form.visibility_until || ""}
                    onChange={(e) => setForm({ ...form, visibility_until: e.target.value })}
                    data-testid="visibility-until"
                    className={inputCls}
                  />
                </Field>
              )}
            </Section>
          )}

          {/* 5c. Per-occurrence themes */}
          {form.recurrence !== "none" && occurrences.length > 0 && (
            <Section title="Per-occurrence themes">
              <p className="text-xs text-[#A3A3A3] mb-4">
                Give each date its own theme <em>and</em> optionally its own photo (e.g. Country &amp;
                Western in July with a country photo, 60s Night in August with a groovy photo).
                Leave the theme blank to reuse the base theme{form.theme ? ` "${form.theme}"` : ""}
                {" "}and leave the photo empty to keep the base photo.
              </p>
              <div className="space-y-3">
                {occurrences.map((occ) => {
                  const override = form.occurrence_overrides.find((o) => o.date === occ.date);
                  const themeValue = override?.theme ?? "";
                  const photoValue = override?.photo_data_uri || null;

                  const upsert = (patch) => {
                    setForm((f) => {
                      const others = f.occurrence_overrides.filter((o) => o.date !== occ.date);
                      const current = f.occurrence_overrides.find((o) => o.date === occ.date) || {
                        date: occ.date,
                      };
                      const next = { ...current, ...patch };
                      const isEmpty =
                        !(next.theme && String(next.theme).trim()) && !next.photo_data_uri;
                      if (isEmpty) {
                        return { ...f, occurrence_overrides: others };
                      }
                      return { ...f, occurrence_overrides: [...others, next] };
                    });
                  };

                  const readPhoto = (file) => {
                    if (!file) return;
                    if (file.size > 4 * 1024 * 1024) {
                      toast.error("Image is too large (max 4 MB).");
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => upsert({ photo_data_uri: reader.result });
                    reader.readAsDataURL(file);
                  };

                  return (
                    <div
                      key={occ.date}
                      data-testid={`occ-row-${occ.date}`}
                      className="grid grid-cols-1 md:grid-cols-[130px_1fr_130px] gap-3 items-center border border-white/10 p-3"
                    >
                      <div className="text-[11px] uppercase tracking-[0.22em] text-[#D9A6F5]">
                        {new Date(occ.date + "T00:00:00").toLocaleDateString("en-GB", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                      <Input
                        type="text"
                        placeholder={form.theme || "Custom theme for this date"}
                        value={themeValue}
                        data-testid={`occ-theme-${occ.date}`}
                        onChange={(e) => upsert({ theme: e.target.value })}
                        className={inputCls}
                      />

                      {/* Photo picker */}
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-14 border border-white/15 bg-black flex items-center justify-center overflow-hidden shrink-0">
                          {photoValue ? (
                            <img
                              src={photoValue}
                              alt=""
                              className="w-full h-full object-cover"
                              data-testid={`occ-photo-preview-${occ.date}`}
                            />
                          ) : form.photo_data_uri ? (
                            <img
                              src={form.photo_data_uri}
                              alt=""
                              className="w-full h-full object-cover opacity-40"
                              title="Using base event photo"
                            />
                          ) : (
                            <ImageIcon size={16} className="text-[#A3A3A3]/40" />
                          )}
                        </div>
                        <div className="flex-1 flex flex-col gap-1">
                          <label
                            className="cursor-pointer inline-flex items-center justify-center gap-1 border border-white/15 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-2 py-1.5 text-[9px] uppercase tracking-[0.2em] font-bold transition-colors"
                            data-testid={`occ-photo-upload-${occ.date}`}
                          >
                            <ImageIcon size={10} />
                            {photoValue ? "Change" : "Custom photo"}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => readPhoto(e.target.files?.[0] || null)}
                            />
                          </label>
                          {photoValue && (
                            <button
                              type="button"
                              onClick={() => upsert({ photo_data_uri: null })}
                              data-testid={`occ-photo-clear-${occ.date}`}
                              className="inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-red-400"
                            >
                              Use base photo
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* 6. Photo */}
          <Section title="Photo">
            <button
              type="button"
              disabled={isNew}
              onClick={() => inputRef.current?.click()}
              data-testid="event-photo-upload"
              className={`w-full aspect-[16/9] border border-dashed border-white/15 hover:border-[#D9A6F5]/60 bg-black flex items-center justify-center overflow-hidden transition-colors ${
                isNew ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              {form.photo_data_uri ? (
                <img src={form.photo_data_uri} alt="Event" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[#A3A3A3]">
                  {uploading ? <Loader2 size={20} className="animate-spin text-[#D9A6F5]" /> : <Upload size={22} />}
                  <div className="text-xs uppercase tracking-[0.22em]">
                    {isNew ? "Save the event first" : "Click to upload photo"}
                  </div>
                </div>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              data-testid="event-photo-input"
              className="hidden"
              onChange={(e) => uploadPhoto(e.target.files?.[0] || null)}
            />
          </Section>

          {/* 7. On-sale */}
          <Section title="When do tickets go on sale?">
            <Field label="Sale window">
              <select
                data-testid="event-on-sale"
                value={form.on_sale}
                onChange={(e) => setForm({ ...form, on_sale: e.target.value })}
                className={inputCls}
              >
                {ON_SALE_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id} className="bg-black">{o.label}</option>
                ))}
              </select>
            </Field>
            {form.on_sale === "specific_date" && (
              <Field label="On-sale date *">
                <Input
                  type="date"
                  data-testid="event-on-sale-date"
                  value={form.on_sale_date}
                  onChange={(e) => setForm({ ...form, on_sale_date: e.target.value })}
                  className={inputCls}
                  required
                />
              </Field>
            )}
          </Section>

          {/* 8. Audiences */}
          <Section title="Event open to">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {AUDIENCES.map((a) => {
                const on = form.audiences.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`event-audience-${a.id}`}
                    onClick={() => toggleAudience(a.id)}
                    className={`px-3 py-3 text-xs uppercase tracking-[0.18em] font-bold border transition-colors ${
                      on
                        ? "bg-[#D9A6F5] text-black border-[#D9A6F5]"
                        : "bg-black text-[#F5F5F0] border-white/15 hover:border-[#D9A6F5]/60"
                    }`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* 9. Pricing */}
          {form.audiences.length > 0 && (
            <Section title="Pricing per audience">
              <div className="space-y-3">
                {form.audiences.map((aid) => {
                  const label = AUDIENCES.find((a) => a.id === aid)?.label || aid;
                  return (
                    <div key={aid} className="grid grid-cols-[1fr_180px] gap-3 items-center">
                      <div className="text-sm text-[#F5F5F0]">{label}</div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] text-sm">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          data-testid={`event-price-${aid}`}
                          value={form.pricing[aid] ?? ""}
                          onChange={(e) => setPrice(aid, e.target.value)}
                          className={`${inputCls} pl-7`}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* 9b. Membership requirement (only when venue has digital memberships turned on) */}
          {me?.venue?.memberships_enabled && (
            <Section title="Membership requirement">
              <label
                className="flex items-start gap-4 border border-white/10 hover:border-[#D9A6F5]/40 p-4 cursor-pointer transition-colors"
                data-testid="event-require-membership-wrap"
              >
                <input
                  type="checkbox"
                  checked={!!form.require_membership}
                  onChange={(e) => setForm({ ...form, require_membership: e.target.checked })}
                  data-testid="event-require-membership"
                  className="mt-1 h-4 w-4 accent-[#D9A6F5]"
                />
                <div>
                  <div className="text-sm text-[#F5F5F0]">
                    Require an active membership to purchase tickets
                  </div>
                  <div className="mt-1 text-[11px] text-[#A3A3A3] leading-relaxed">
                    Buyers will be prompted to enter their membership number at checkout. Prices
                    below apply per membership type and override the &ldquo;per audience&rdquo; pricing above.
                  </div>
                </div>
              </label>

              {form.require_membership && (
                <div className="space-y-3 mt-6" data-testid="event-membership-pricing">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3]">
                    Price per membership type
                  </div>
                  {MEMBERSHIP_TYPES.map((m) => (
                    <div key={m.id} className="grid grid-cols-[1fr_180px] gap-3 items-center">
                      <div className="text-sm text-[#F5F5F0]">{m.label}</div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] text-sm">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          data-testid={`event-mprice-${m.id}`}
                          value={form.membership_pricing?.[m.id] ?? ""}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              membership_pricing: {
                                ...(f.membership_pricing || {}),
                                [m.id]: e.target.value === "" ? "" : Number(e.target.value),
                              },
                            }))
                          }
                          className={`${inputCls} pl-7`}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Capacity & sales */}
          <Section title="Capacity & tickets sold">
            <p className="text-sm text-[#A3A3A3] mb-2">
              Used for the weekly email badges: <span className="text-[#F5F5F0]">On Sale</span> (&lt;70%),{" "}
              <span className="text-[#F5F5F0]">Limited</span> (70–90%),{" "}
              <span className="text-[#F5F5F0]">Very Limited</span> (&gt;90%),{" "}
              <span className="text-[#F5F5F0]">Sold Out</span> (100%). Leave capacity blank for unlimited.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Total capacity">
                <Input
                  type="number"
                  min="0"
                  data-testid="event-capacity"
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                  placeholder="e.g. 250"
                  className={inputCls}
                />
              </Field>
              <Field label="Tickets sold so far">
                <Input
                  type="number"
                  min="0"
                  data-testid="event-tickets-sold"
                  value={form.tickets_sold}
                  onChange={(e) => setForm({ ...form, tickets_sold: e.target.value })}
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          {/* 10. Discount codes */}
          <Section title="Discount codes">
            <div className="space-y-3">
              {form.discount_codes.length === 0 && (
                <div className="text-sm text-[#A3A3A3]/60 uppercase tracking-[0.2em] text-center py-4">
                  No discount codes yet
                </div>
              )}
              {form.discount_codes.map((d, i) => (
                <div key={i} className="grid grid-cols-[1fr_180px_auto] gap-3 items-center">
                  <Input
                    data-testid={`discount-code-${i}`}
                    value={d.code}
                    onChange={(e) => updateDiscount(i, { code: e.target.value })}
                    placeholder="Code (e.g. LAUNCH)"
                    className={inputCls}
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] text-sm">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid={`discount-saving-${i}`}
                      value={d.saving}
                      onChange={(e) => updateDiscount(i, { saving: e.target.value })}
                      placeholder="Saving"
                      className={`${inputCls} pl-7`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDiscount(i)}
                    data-testid={`discount-remove-${i}`}
                    aria-label="Remove discount"
                    className="text-[#A3A3A3] hover:text-red-400 p-2"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="add-discount-btn"
                onClick={addDiscount}
                className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Plus size={12} /> Add discount code
              </button>
            </div>
          </Section>

          <div className="pt-4">
            <button
              type="submit"
              disabled={saving}
              data-testid="event-save"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-8 py-4 text-sm uppercase tracking-[0.22em] font-bold transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : null}
              {isNew ? "Create Event" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </PortalShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 space-y-4">
      <div className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">{label}</div>
      {children}
    </div>
  );
}
